import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import {
  ScrollbarStyled,
  ScrollWrap,
  OuterContainer,
  InnerHandle,
  InnerContainer,
  ContentWrapper,
  ScrollHandle,
} from './styles/ScrollbarStyled';

class Scrollbar extends PureComponent {
  constructor(props) {
    super(props);

    this.scrollbarYWidth = 0;
    this.node = null;
    this.innerContainer = null;
    this.state = {
      scrollPos: 0,
      onDrag: false,
      outerBlock: false,
    };
  }

  componentDidMount() {
    const { scrollTo } = this.props;
    setTimeout(() => {
      if (this.innerContainer) {
        this.scrollbarYWidth = this.innerContainer.offsetWidth - this.innerContainer.clientWidth;
      }
    }, 100);
    if (typeof scrollTo !== 'undefined') {
      this.updateScrollPosition(scrollTo.value);
    } else {
      this.forceUpdate();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { scrollPos } = this.state;
    const { getScrollTarget, forceUpdateScrollBar } = this.props;

    const prevContentHeight = this.contentHeight;
    const prevVisibleHeight = this.visibleHeight;
    const reachedBottomOnPrevRender = prevState.scrollPos >= prevContentHeight - prevVisibleHeight;

    this.contentHeight = this.innerContainer.scrollHeight;
    this.scrollbarYWidth = this.innerContainer.offsetWidth - this.innerContainer.clientWidth;
    this.visibleHeight = this.innerContainer.clientHeight;
    this.scrollRatio = this.contentHeight ? this.visibleHeight / this.contentHeight : 1;
    const reachedBottomOnCurrentRender = scrollPos >= this.contentHeight - this.visibleHeight;
    const contentResized = prevContentHeight !== this.contentHeight;

    this.toggleScrollIfNeeded();
    getScrollTarget(this.innerContainer);

    if (this.props.freezePosition || prevProps.freezePosition) {
      this.adjustFreezePosition(prevProps);
    }
    if (typeof this.props.scrollTo !== 'undefined' && this.props.scrollTo !== prevProps.scrollTo) {
      this.updateScrollPosition(this.props.scrollTo.value, true);
    } else if (
      this.props.keepAtBottom &&
      contentResized &&
      reachedBottomOnPrevRender &&
      !reachedBottomOnCurrentRender
    ) {
      this.updateScrollPosition(this.contentHeight - this.visibleHeight);
    }

    if (
      prevProps.forceUpdateScrollBar.id !== forceUpdateScrollBar.id ||
      prevProps.forceUpdateScrollBar.state !== forceUpdateScrollBar.state
    )
      this.forceUpdate(); // activate forceUpdate if inner collapsible component state changes
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.onHandleDrag);
    document.removeEventListener('mouseup', this.onHandleDragEnd);
  }

  onScroll = (event) => {
    const { onScroll } = this.props;

    if (this.props.freezePosition) {
      return;
    }
    this.adjustCustomScrollPosToContentPos(event.currentTarget.scrollTop);
    if (onScroll) {
      onScroll(this.innerContainer);
    }
  };

  onMouseDown = (event) => {
    if (!this.hasScroll || !this.isMouseEventOnScrollHandle(event)) {
      return;
    }

    this.startDragHandlePos = this.getScrollHandleStyle().top;
    this.startDragMousePos = event.pageY;
    this.setState({
      onDrag: true,
    });
    document.addEventListener('mousemove', this.onHandleDrag);
    document.addEventListener('mouseup', this.onHandleDragEnd);
  };

  onHandleDrag = (event) => {
    event.preventDefault();
    const mouseDeltaY = event.pageY - this.startDragMousePos;
    const handleTopPosition = this.ensureWithinLimits(
      this.startDragHandlePos + mouseDeltaY,
      0,
      this.visibleHeight - this.scrollHandleHeight
    );
    const newScrollValue = this.getScrollValueFromHandlePosition(handleTopPosition);
    this.updateScrollPosition(newScrollValue);
  };

  onHandleDragEnd = (e) => {
    this.setState({
      onDrag: false,
    });
    e.preventDefault();
    document.removeEventListener('mousemove', this.onHandleDrag);
    document.removeEventListener('mouseup', this.onHandleDragEnd);
  };

  getOuterContainerStyle = () => ({
    height: this.props.heightRelativeToParent || '100%',
  });

  getScrollStyles = () => {
    const { heightRelativeToParent, freezePosition, maxHeight } = this.props;
    const scrollSize = this.scrollbarYWidth || 20;
    const marginKey = 'marginRight';
    const innerContainerStyle = {
      [marginKey]: -1 * (this.scrollbarYWidth ? this.scrollbarYWidth + 5 : 20),
      height: heightRelativeToParent || '100%',
      maxHeight: maxHeight || '100%',
    };
    const contentWrapperStyle = {
      [marginKey]: this.scrollbarYWidth ? 0 : scrollSize,
      height: heightRelativeToParent || '100%',
      overflowY: freezePosition ? 'hidden' : 'visible',
    };

    return {
      innerContainer: innerContainerStyle,
      contentWrapper: contentWrapperStyle,
    };
  };

  getRootStyles = () => {
    const { heightRelativeToParent } = this.props;
    const result = {};

    if (heightRelativeToParent) {
      result.height = heightRelativeToParent;
    }

    return result;
  };

  getScrollValueFromHandlePosition = (handlePosition) => handlePosition / this.scrollRatio;

  getScrollHandleStyle = () => {
    const handlePosition = this.state.scrollPos * this.scrollRatio;
    this.scrollHandleHeight = this.visibleHeight * this.scrollRatio;
    return {
      height: this.scrollHandleHeight,
      top: handlePosition,
    };
  };

  setScrollTop = (target, value) => {
    if (target) {
      target.scrollTop = value;
    }
  };

  isMouseEventOnScrollHandle = (event) => {
    const scrollHandle = this.scrollHandle;
    return this.isEventPosOnDomNode(event, scrollHandle);
  };

  updateScrollPosition = (scrollValue, isScrollTo) => {
    this.setScrollTop(this.innerContainer, scrollValue);
    this.setState(
      {
        scrollPos: scrollValue,
      },
      () => {
        if (isScrollTo) {
          this.props.onScrollTo();
        }
      }
    );
  };

  blockOuterScroll = (e) => {
    const { outerBlock } = this.state;

    if (outerBlock) {
      const contentNode = e.currentTarget;
      const totalHeight = e.currentTarget.scrollHeight;
      const maxScroll = totalHeight - e.currentTarget.offsetHeight;
      const delta = e.deltaY % 3 ? e.deltaY : e.deltaY * 10;
      if (contentNode.scrollTop + delta <= 0) {
        this.setScrollTop(contentNode, 0);
        e.preventDefault();
      } else if (contentNode.scrollTop + delta >= maxScroll) {
        this.setScrollTop(contentNode, maxScroll);
        e.preventDefault();
      }
      e.stopPropagation();
    }
  };

  adjustCustomScrollPosToContentPos = (scrollPosition) => {
    this.setState({
      scrollPos: scrollPosition,
    });
  };

  toggleScrollIfNeeded = () => {
    const shouldHaveScroll = this.contentHeight - this.visibleHeight > 1;
    if (this.hasScroll !== shouldHaveScroll) {
      this.hasScroll = shouldHaveScroll;
      this.forceUpdate();
    }
  };

  adjustFreezePosition = (prevProps) => {
    const { freezePosition } = this.props;
    const { scrollPos } = this.state;
    const contentWrapper = this.contentWrapper;

    if (freezePosition) {
      this.setScrollTop(contentWrapper, scrollPos);
    }

    if (prevProps.freezePosition) {
      this.setScrollTop(this.innerContainer, scrollPos);
    }
  };

  ensureWithinLimits = (value, min, max) => {
    min = !min && min !== 0 ? value : min;
    max = !max && max !== 0 ? value : max;
    if (min > max) {
      return value;
    }
    if (value < min) {
      return min;
    }
    if (value > max) {
      return max;
    }
    return value;
  };

  isEventPosOnDomNode = (event, domNode) => {
    if (domNode) {
      const nodeClientRect = domNode.getBoundingClientRect();
      return (
        event.clientX > nodeClientRect.left &&
        event.clientX < nodeClientRect.left + nodeClientRect.width &&
        event.clientY > nodeClientRect.top &&
        event.clientY < nodeClientRect.top + nodeClientRect.height
      );
    }
    return false;
  };

  enforceMinHandleHeight = (calculatedStyle) => {
    const minHeight = this.props.minScrollHandleHeight;
    if (calculatedStyle.height >= minHeight) {
      return calculatedStyle;
    }

    const diffHeightBetweenMinAndCalculated = minHeight - calculatedStyle.height;
    const scrollPositionToAvailableScrollRatio =
      this.state.scrollPos / (this.contentHeight - this.visibleHeight);
    const scrollHandlePosAdjustmentForMinHeight =
      diffHeightBetweenMinAndCalculated * scrollPositionToAvailableScrollRatio;
    const handlePosition = calculatedStyle.top - scrollHandlePosAdjustmentForMinHeight;

    return {
      height: minHeight,
      top: handlePosition,
    };
  };

  assignCustomScrollRef = (ref) => {
    this.customScrollbar = ref;
  };

  assignScrollHandleRef = (ref) => {
    this.scrollHandle = ref;
  };

  assignInnerContainerRef = (ref) => {
    this.innerContainer = ref;
  };

  assignContentWrapperRef = (ref) => {
    this.contentWrapper = ref;
  };

  render() {
    const { children, type } = this.props;
    const { onDrag, scrollThumb } = this.state;
    const scrollStyles = this.getScrollStyles();
    const rootStyle = this.getRootStyles();
    const scrollHandleStyle = this.enforceMinHandleHeight(this.getScrollHandleStyle());

    return (
      <ScrollbarStyled scrollThumb={scrollThumb} dragState={onDrag} style={rootStyle}>
        <OuterContainer style={this.getOuterContainerStyle()} onMouseDown={this.onMouseDown}>
          {this.hasScroll ? (
            <ScrollWrap ref={this.assignCustomScrollRef} dragState={onDrag} key="scrollbar">
              <ScrollHandle ref={this.assignScrollHandleRef} style={scrollHandleStyle}>
                <InnerHandle type={type} dragState={onDrag} />
              </ScrollHandle>
            </ScrollWrap>
          ) : null}
          <InnerContainer
            ref={this.assignInnerContainerRef}
            id="innerScrollContainer"
            style={scrollStyles.innerContainer}
            onScroll={this.onScroll}
            onWheel={this.blockOuterScroll}
          >
            <ContentWrapper ref={this.assignContentWrapperRef} style={scrollStyles.contentWrapper}>
              {children}
            </ContentWrapper>
          </InnerContainer>
        </OuterContainer>
      </ScrollbarStyled>
    );
  }
}

Scrollbar.propTypes = {
  children: PropTypes.any,
  heightRelativeToParent: PropTypes.string,
  minScrollHandleHeight: PropTypes.number,
  onScroll: PropTypes.func,
  freezePosition: PropTypes.bool,
  scrollTo: PropTypes.shape({
    value: PropTypes.number,
  }),
  keepAtBottom: PropTypes.bool,
  type: PropTypes.string,
  maxHeight: PropTypes.string,
  getScrollTarget: PropTypes.func,
  onScrollTo: PropTypes.func,
  forceUpdateScrollBar: PropTypes.object,
};

Scrollbar.defaultProps = {
  minScrollHandleHeight: 38,
  keepAtBottom: false,
  scrollTo: { value: 0 },
  freezePosition: false,
  onScroll: Function.prototype,
  heightRelativeToParent: '',
  children: '',
  type: null,
  maxHeight: null,
  getScrollTarget: Function.prototype,
  onScrollTo: Function.prototype,
  forceUpdateScrollBar: { id: 0, state: false },
};

export default Scrollbar;
